/** Relay transport: the recorder's own bitstream, decoded in the page.
 *
 * The relay forwards access units exactly as the recorder sent them, and
 * WebCodecs decodes them onto a canvas. There is no playlist to poll, no
 * segment to fill and no transcode anywhere, which is why a picture appears in
 * a fraction of the time HLS or WHEP take: on these recorders a fresh RTSP
 * session begins with a keyframe, so the very first frame delivered is
 * displayable.
 *
 * A `<video>` element would buffer and present frames on its own clock, but
 * getting the bitstream into one means muxing MP4 on the server. That was
 * built, measured and removed: the only thing it bought was the pacing below.
 *
 * Pacing matters. Painting each frame the moment it decodes looks fine on a
 * quiet link and stutters on a real one, because network jitter lands directly
 * on screen. Frames are held briefly and presented against a clock instead, and
 * the queue is bounded so a hiccup costs a small jump rather than an
 * ever-growing delay.
 */

export type DirectConnection = {
  /** Jump to an instant (epoch ms). Recorded sources only. */
  seek: (startMs: number) => void;
  /** End the current stream but keep the socket, so a seek stays cheap. */
  stop: () => void;
  /** Media time of the last painted frame, ms since the current seek. */
  mediaTimeMs: () => number;
  close: () => void;
};

export type DirectOpts = {
  /** Epoch ms to start recorded playback from. Omit to wait for a `seek`. */
  startMs?: number;
  /** Live sources have no start time and begin as soon as the socket opens. */
  live?: boolean;
  /** Ask the recorder for the window faster than real time. */
  scale?: number;
  /** How long to hold frames before presenting, to absorb network jitter. */
  bufferMs?: number;
  /** Fired on the first painted frame, for latency measurement. */
  onFirstFrame?: () => void;
  onError?: (message: string) => void;
  /** A live viewer fell too far behind and the relay ended the stream. */
  onBehind?: () => void;
  /** The source ran out: a recorded window reached its end, or live stopped. */
  onEnded?: () => void;
  /** The source dropped and the relay is retrying. */
  onReconnecting?: (attempt: number) => void;
};

/** Enough to ride out a stall without letting latency grow unnoticed. */
const DEFAULT_BUFFER_MS = 120;
/** Decoded frames hold GPU memory, so the queue is hard-bounded. */
const MAX_QUEUED_FRAMES = 12;
/** Access units held while configure() is still in flight. */
const MAX_PENDING_CHUNKS = 90;

type PendingChunk = {
  type: EncodedVideoChunkType;
  timestamp: number;
  data: Uint8Array;
};

function isKeyRequired(err: unknown): boolean {
  const text = err instanceof Error ? err.message : String(err);
  return /key frame is required/i.test(text);
}

function chunkFromRelay(buf: ArrayBuffer): PendingChunk {
  const bytes = new Uint8Array(buf);
  return {
    type: bytes[0] & 0x01 ? "key" : "delta",
    timestamp: Number(new DataView(buf).getBigUint64(2)),
    // Copy: the WebSocket buffer is reused for the next message.
    data: bytes.subarray(10).slice(),
  };
}

type Queued = { frame: VideoFrame; timestampUs: number };

export function directSupported(): boolean {
  return typeof window !== "undefined" && "VideoDecoder" in window;
}

/** Codec strings to try for what the relay reported, best guess first.
 *
 * Profile compatibility and constraint bytes are not worth reconstructing
 * exactly; the first string the browser accepts wins, and a slightly generous
 * level is harmless.
 */
function codecCandidates(fmt: {
  codec: string;
  profile_idc?: number;
  tier?: number;
  level_idc?: number;
}): string[] {
  if (fmt.codec === "H264") {
    return ["avc1.640028", "avc1.4D4028", "avc1.42E01E"];
  }
  const tier = fmt.tier ? "H" : "L";
  const level = fmt.level_idc || 153;
  const profile = fmt.profile_idc || 1;
  return [
    `hev1.${profile}.6.${tier}${level}.B0`,
    `hvc1.${profile}.6.${tier}${level}.B0`,
    `hev1.${profile}.6.${tier}${level}.90`,
    "hev1.1.6.L153.B0",
  ];
}

async function pickCodec(fmt: Parameters<typeof codecCandidates>[0]) {
  for (const codec of codecCandidates(fmt)) {
    const config: VideoDecoderConfig = { codec, optimizeForLatency: true };
    try {
      const probe = await VideoDecoder.isConfigSupported(config);
      if (probe.supported) return config;
    } catch {
      /* try the next shape */
    }
  }
  return null;
}

/** Grabs single stills from a recorder, reusing one socket and one decoder.
 *
 * The server-side alternative pulls four seconds of video and runs a GPU
 * transcode for one JPEG, which costs more than playing the moment does. Here
 * the relay is asked for the instant, the first keyframe is decoded -- it is
 * always the first thing a recorder sends -- and the stream is stopped again.
 */
export function createStillGrabber(session: string) {
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  const ws = new WebSocket(
    `${scheme}//${window.location.host}/stream/ws?session=${encodeURIComponent(session)}`,
  );
  ws.binaryType = "arraybuffer";

  const canvas = document.createElement("canvas");
  const paint = canvas.getContext("2d");
  let decoder: VideoDecoder | null = null;
  let config: VideoDecoderConfig | null = null;
  let generation = -1;
  let configEpoch = 0;
  let awaitKey = true;
  let decodeReady = false;
  let pending: PendingChunk[] = [];
  let closed = false;
  let waiting: ((dataUrl: string | null) => void) | null = null;

  const settle = (value: string | null) => {
    const resolve = waiting;
    waiting = null;
    resolve?.(value);
  };

  const onFrame = (frame: VideoFrame) => {
    if (closed || !waiting) {
      frame.close();
      return;
    }
    // One frame is all a still needs; stop the recorder pulling for it.
    const width = 480;
    const height = Math.max(
      1,
      Math.round((frame.displayHeight / frame.displayWidth) * width),
    );
    canvas.width = width;
    canvas.height = height;
    paint?.drawImage(frame, 0, 0, width, height);
    frame.close();
    try {
      ws.send(JSON.stringify({ type: "stop" }));
    } catch {
      /* socket already gone */
    }
    settle(canvas.toDataURL("image/jpeg", 0.72));
  };

  const feedStill = (chunk: PendingChunk) => {
    if (!decodeReady || !decoder || decoder.state !== "configured") {
      pending.push(chunk);
      if (pending.length > MAX_PENDING_CHUNKS) pending.shift();
      return;
    }
    if (awaitKey && chunk.type !== "key") return;
    try {
      decoder.decode(new EncodedVideoChunk(chunk));
      if (chunk.type === "key") awaitKey = false;
    } catch (e) {
      if (isKeyRequired(e)) {
        awaitKey = true;
        return;
      }
      settle(null);
    }
  };

  ws.addEventListener("message", (ev: MessageEvent) => {
    if (closed) return;
    if (typeof ev.data === "string") {
      const msg = JSON.parse(ev.data) as Record<string, unknown>;
      if (msg.type === "format") {
        generation = Number(msg.generation);
        const epoch = configEpoch;
        awaitKey = true;
        decodeReady = false;
        pending = [];
        void (async () => {
          config = config ?? (await pickCodec(msg as Parameters<typeof codecCandidates>[0]));
          if (!config || closed) return settle(null);
          if (epoch !== configEpoch) return;
          if (decoder && decoder.state === "closed") decoder = null;
          if (!decoder) {
            decoder = new VideoDecoder({ output: onFrame, error: () => settle(null) });
          }
          decoder.configure(config);
          decodeReady = true;
          const queued = pending;
          pending = [];
          for (const chunk of queued) feedStill(chunk);
        })();
      } else if (msg.type === "error" || msg.type === "ended") {
        settle(null);
      }
      return;
    }
    const bytes = new Uint8Array(ev.data as ArrayBuffer);
    if (bytes[1] !== generation) return;
    feedStill(chunkFromRelay(ev.data as ArrayBuffer));
  });
  ws.addEventListener("close", () => settle(null));
  ws.addEventListener("error", () => settle(null));

  return {
    /** The recorded picture at `instantMs`, as a JPEG data URL. */
    async grab(instantMs: number, timeoutMs = 4000): Promise<string | null> {
      if (closed) return null;
      if (ws.readyState === WebSocket.CONNECTING) {
        await new Promise<void>((resolve) => {
          ws.addEventListener("open", () => resolve(), { once: true });
          ws.addEventListener("error", () => resolve(), { once: true });
        });
      }
      if (closed || ws.readyState !== WebSocket.OPEN) return null;
      // A newer hover supersedes whatever was being fetched.
      settle(null);
      configEpoch += 1;
      pending = [];
      awaitKey = true;
      decodeReady = false;
      generation = -1;
      if (decoder && decoder.state === "configured") decoder.reset();
      const answer = new Promise<string | null>((resolve) => {
        waiting = resolve;
      });
      ws.send(JSON.stringify({ type: "seek", start_ms: Math.round(instantMs) }));
      const timer = window.setTimeout(() => settle(null), timeoutMs);
      const value = await answer;
      window.clearTimeout(timer);
      return value;
    },
    close() {
      closed = true;
      settle(null);
      try {
        ws.close();
      } catch {
        /* noop */
      }
      try {
        if (decoder && decoder.state !== "closed") decoder.close();
      } catch {
        /* noop */
      }
      decoder = null;
    },
  };
}

export type StillGrabber = ReturnType<typeof createStillGrabber>;

/**
 * The bitmap is the camera's own pixels (often 4K). Without an explicit CSS
 * size, a flex/grid item will not shrink below that intrinsic size, so a
 * smaller pane — or fullscreen on a 1080p screen — shows only the top-left
 * corner. Fit the element to the host and keep the bitmap for sharpness.
 */
function attachCanvasFit(canvas: HTMLCanvasElement): {
  apply: () => void;
  detach: () => void;
} {
  const apply = () => {
    const host = canvas.parentElement;
    if (!host || canvas.width < 1 || canvas.height < 1) return;
    const pw = host.clientWidth;
    const ph = host.clientHeight;
    if (pw < 1 || ph < 1) return;
    const scale = Math.min(pw / canvas.width, ph / canvas.height);
    canvas.style.width = `${canvas.width * scale}px`;
    canvas.style.height = `${canvas.height * scale}px`;
  };
  const ro = new ResizeObserver(apply);
  if (canvas.parentElement) ro.observe(canvas.parentElement);
  document.addEventListener("fullscreenchange", apply);
  apply();
  return {
    apply,
    detach: () => {
      ro.disconnect();
      document.removeEventListener("fullscreenchange", apply);
    },
  };
}

export function connectDirect(
  session: string,
  canvas: HTMLCanvasElement,
  opts?: DirectOpts,
): DirectConnection {
  const bufferMs = opts?.bufferMs ?? DEFAULT_BUFFER_MS;
  const paint = canvas.getContext("2d");
  const canvasFit = attachCanvasFit(canvas);
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  const ws = new WebSocket(
    `${scheme}//${window.location.host}/stream/ws?session=${encodeURIComponent(session)}`,
  );
  ws.binaryType = "arraybuffer";

  let decoder: VideoDecoder | null = null;
  let config: VideoDecoderConfig | null = null;
  let generation = -1;
  let configEpoch = 0;
  let awaitKey = true;
  let decodeReady = false;
  let pending: PendingChunk[] = [];
  let closed = false;
  let painted = false;
  let raf = 0;
  let presentedUs = 0;
  const queue: Queued[] = [];
  // Maps stream time onto wall-clock time; re-established after any jump.
  let origin: { wallMs: number; timestampUs: number } | null = null;

  const draw = (frame: VideoFrame) => {
    if (canvas.width !== frame.displayWidth || canvas.height !== frame.displayHeight) {
      canvas.width = frame.displayWidth;
      canvas.height = frame.displayHeight;
    }
    paint?.drawImage(frame, 0, 0);
    canvasFit.apply();
    if (!painted) {
      painted = true;
      opts?.onFirstFrame?.();
    }
  };

  const present = () => {
    raf = 0;
    if (closed) return;
    if (queue.length === 0) return;

    if (origin === null) {
      // Start the clock behind the first frame by the buffer, so there is
      // something in hand before anything is due.
      origin = {
        wallMs: performance.now() + bufferMs,
        timestampUs: queue[0].timestampUs,
      };
    }
    const dueUs =
      origin.timestampUs + (performance.now() - origin.wallMs) * 1000;

    let toDraw: Queued | null = null;
    while (queue.length > 0 && queue[0].timestampUs <= dueUs) {
      const item = queue.shift()!;
      // Only the newest due frame is worth painting; older ones are already
      // late and drawing them would just cost time.
      toDraw?.frame.close();
      toDraw = item;
    }
    if (toDraw) {
      presentedUs = toDraw.timestampUs;
      draw(toDraw.frame);
      toDraw.frame.close();
    }
    schedule();
  };

  const schedule = () => {
    if (raf || closed) return;
    raf = requestAnimationFrame(present);
  };

  const onFrame = (frame: VideoFrame) => {
    if (closed) {
      frame.close();
      return;
    }
    queue.push({ frame, timestampUs: frame.timestamp ?? 0 });
    if (queue.length > MAX_QUEUED_FRAMES) {
      // The link or the tab stalled. Keep the newest frame and restart the
      // clock on it: a visible jump, but latency returns to the buffer instead
      // of compounding for the rest of the session.
      while (queue.length > 1) queue.shift()!.frame.close();
      origin = null;
    }
    schedule();
  };

  const dropQueue = () => {
    while (queue.length) queue.shift()!.frame.close();
    origin = null;
  };

  const abandonInFlight = () => {
    configEpoch += 1;
    pending = [];
    awaitKey = true;
    decodeReady = false;
    generation = -1;
    dropQueue();
  };

  const onDecoderError = (err: unknown) => {
    if (isKeyRequired(err)) {
      awaitKey = true;
      try {
        if (decoder && decoder.state === "configured") decoder.reset();
      } catch {
        /* already closed */
      }
      return;
    }
    opts?.onError?.(err instanceof Error ? err.message : String(err));
  };

  const feed = (chunk: PendingChunk) => {
    if (!decodeReady || !decoder || decoder.state !== "configured") {
      pending.push(chunk);
      if (pending.length > MAX_PENDING_CHUNKS) {
        const firstKey = pending.findIndex((c) => c.type === "key");
        pending.splice(0, firstKey > 0 ? firstKey : 1);
      }
      return;
    }
    if (awaitKey && chunk.type !== "key") return;
    try {
      decoder.decode(new EncodedVideoChunk(chunk));
      if (chunk.type === "key") awaitKey = false;
    } catch (e) {
      onDecoderError(e);
    }
  };

  const configure = async (
    fmt: Parameters<typeof codecCandidates>[0],
    epoch: number,
  ) => {
    config = config ?? (await pickCodec(fmt));
    if (closed || epoch !== configEpoch) return;
    if (!config) {
      opts?.onError?.(`this browser cannot decode ${fmt.codec}`);
      return;
    }
    if (decoder && decoder.state === "closed") decoder = null;
    if (!decoder) {
      decoder = new VideoDecoder({
        output: onFrame,
        error: (e) => onDecoderError(e),
      });
    }
    awaitKey = true;
    decoder.configure(config);
    decodeReady = true;
    const queued = pending;
    pending = [];
    for (const chunk of queued) feed(chunk);
  };

  ws.addEventListener("message", (ev: MessageEvent) => {
    if (closed) return;
    if (typeof ev.data === "string") {
      const msg = JSON.parse(ev.data) as Record<string, unknown>;
      if (msg.type === "format") {
        // A new generation is a new coded video sequence, from a seek or from a
        // reconnect after the source dropped. Either way nothing queued from the
        // old one should paint, and the presentation clock restarts with it.
        generation = Number(msg.generation);
        dropQueue();
        pending = [];
        awaitKey = true;
        decodeReady = false;
        const epoch = configEpoch;
        void configure(msg as Parameters<typeof codecCandidates>[0], epoch);
      } else if (msg.type === "reconnecting") {
        opts?.onReconnecting?.(Number(msg.attempt) || 1);
      } else if (msg.type === "behind") {
        opts?.onBehind?.();
      } else if (msg.type === "ended") {
        if (Number(msg.generation) === generation) opts?.onEnded?.();
      } else if (msg.type === "error") {
        opts?.onError?.(String(msg.detail ?? "playback failed"));
      }
      return;
    }
    const bytes = new Uint8Array(ev.data as ArrayBuffer);
    // Media from a seek we have already abandoned cannot be recalled once it
    // is in the socket buffer, so it is tagged and dropped here.
    if (bytes[1] !== generation) return;
    feed(chunkFromRelay(ev.data as ArrayBuffer));
  });

  const send = (payload: Record<string, unknown>) => {
    const write = () => ws.send(JSON.stringify(payload));
    if (ws.readyState === WebSocket.OPEN) write();
    else ws.addEventListener("open", write, { once: true });
  };

  // A recorded session opened ahead of the first click has no instant to play
  // yet. Asking for one anyway sends the recorder a window starting at epoch 0,
  // which it answers with `DESCRIBE returned 404` on every page load.
  if (opts?.live) {
    send({ type: "open" });
  } else if (opts?.startMs != null) {
    send({ type: "seek", start_ms: opts.startMs, scale: opts.scale ?? 1 });
  }

  return {
    mediaTimeMs: () => presentedUs / 1000,
    stop: () => {
      abandonInFlight();
      if (decoder && decoder.state === "configured") decoder.reset();
      send({ type: "stop" });
    },
    seek: (startMs: number) => {
      // Drop frames from the moment being left before they can paint, and
      // invalidate in-flight media; the relay's next format message brings the
      // new generation. Clearing the canvas matters too: otherwise the last
      // picture of the previous moment stays on screen looking like this one.
      abandonInFlight();
      painted = false;
      presentedUs = 0;
      paint?.clearRect(0, 0, canvas.width, canvas.height);
      if (decoder && decoder.state === "configured") decoder.reset();
      send({ type: "seek", start_ms: startMs, scale: opts?.scale ?? 1 });
    },
    close: () => {
      closed = true;
      canvasFit.detach();
      abandonInFlight();
      if (raf) cancelAnimationFrame(raf);
      dropQueue();
      try {
        ws.close();
      } catch {
        /* noop */
      }
      try {
        if (decoder && decoder.state !== "closed") decoder.close();
      } catch {
        /* noop */
      }
      decoder = null;
    },
  };
}
